// services/Recruit/components/OfferLetter/OfferContentEditor.jsx
import { useState } from 'react';
import { Box, Flex, Text, Wrap, WrapItem, Tag, Switch, FormLabel, VStack, Button, Image } from '@chakra-ui/react';
import TextEditor from 'common/components/textEditor/TextEditor';
import { defaultPlaceholders } from 'services/Recruit/data/panel.Data';
import { recruitConstant } from 'services/Recruit/constant';
import Preview from "../../assets/img/preview.png"
import Pencil from "../../assets/img/pencil.png"
import colors from 'common/theme/foundations/colors';
import { toast } from 'react-toastify';
import TemplateSelector from './TemplateSelector';
import DOMPurify from 'dompurify';

const OfferContentEditor = ({
    value,
    isOffer,
    onChange,
    permission = {},
    isOverviewMode = false,
    setContentPreview,
    showPlaceholders = true,
    previewContent = null,
    isEditMode = false,
    showExample = true,
    setFieldValue,
    values
}) => {
    const [showPreview, setShowPreview] = useState(false);

    const insertPlaceholder = (placeholder) => {
        const currentContent = value || '';

        if (!currentContent.trim()) {
            onChange(placeholder.key + ' ');
            return;
        }

        const needsSpaceBefore = !currentContent.match(/[\s\n>]$/);
        const prefix = needsSpaceBefore ? ' ' : '';
        const newContent = currentContent + prefix + placeholder.key + ' ';

        onChange(newContent);
    };
    const handleTemplateSelect = (template, setFieldValue) => {
        setFieldValue('templateId', template._id);
        setFieldValue('offerLetterContent', template.content);
        onChange(template.content);
        generatePreview(template.content);
        toast.success('Template applied');
    };
    // Clean HTML for Preview
    const generatePreview = (content) => {
        if (!content) {
            setContentPreview("");
            return;
        }

        // Remove unnecessary <p><br></p>, <p></p>, empty blocks
        let cleaned = content
            .replace(/<p><br><\/p>/g, "")    // remove empty paragraphs
            .replace(/<p>\s*<\/p>/g, "")     // remove whitespace-only paragraphs
            .replace(/&nbsp;/g, " ")         // convert &nbsp; to space
            .trim();

        setContentPreview(cleaned);
    };

    const handleContentChange = (content) => {
        // Remove tag text and spaces
        const textOnly = content
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/g, " ")
            .trim();

        const isEmpty = textOnly.length === 0;

        const finalValue = isEmpty ? "" : content;

        onChange(finalValue);
        setFieldValue("offerLetterContent", finalValue);

        // 🔥 Auto-update preview clean content
        generatePreview(finalValue);
    };


    return (
        <>
            {isOffer ? <Box>
                <VStack align="stretch" border="1px solid" ml={6} mr={6} p={5}
                    gap={5}
                    borderColor="#CFD6DC"
                    borderRadius="8px">
                    <Flex justify={'space-between'}>
                        <FormLabel fontSize="16px" fontWeight="500">Offer Letter Content <span style={{ color: 'red' }}>{recruitConstant?.asterisk}</span></FormLabel>

                        <Flex>
                            <TemplateSelector
                                isDisabled={!permission?.update || isOverviewMode}
                                isOfferUpdation={true}
                                onSelect={(template) => handleTemplateSelect(template, setFieldValue)}
                                selectedTemplateId={values.templateId}
                            />

                        </Flex>

                    </Flex>
                    {showPlaceholders && (
                        <Box mb={3} pt={2} borderRadius="md">

                            <Flex justify="space-between" align="center" mb={2}>
                                <Text fontSize="14px" fontWeight="400" className='roboto' color={colors.black}>
                                    Dynamic Placeholders
                                </Text>
                                {previewContent && (
                                    <Flex align="center" gap={2}>
                                        <FormLabel htmlFor="preview-toggle" mb={0} fontSize="sm">
                                            Preview Mode
                                        </FormLabel>
                                        <Switch
                                            id="preview-toggle"
                                            isChecked={showPreview}
                                            onChange={(e) => setShowPreview(e.target.checked)}
                                            colorScheme="blue"
                                        />
                                    </Flex>
                                )}
                            </Flex>

                            <Wrap spacing={2} mb={2}>
                                {defaultPlaceholders.map((placeholder) => (
                                    <WrapItem key={placeholder.key}>
                                        <Tag
                                            size="md"
                                            fontSize="14px"
                                            fontWeight="400"
                                            pl={3}
                                            pr={3}
                                            pt={1}
                                            pb={1}

                                            isDisabled={!permission?.update || isOverviewMode}

                                            cursor={!permission?.update || isOverviewMode ? "not-allowed" : "pointer"}
                                            colorScheme="blue"
                                            borderRadius="4px"
                                            variant="subtle"
                                            onClick={() => !(!permission?.update || isOverviewMode) && insertPlaceholder(placeholder)}
                                            title={placeholder.description}

                                            bg={!permission?.update || isOverviewMode ? "#ebebeb" : "#EFF6FF"}
                                            border="1px solid"
                                            borderColor={!permission?.update || isOverviewMode ? "#CBD5E0" : "#BEDBFF"}
                                            color={!permission?.update || isOverviewMode ? "#A0AEC0" : "#285AE7"}

                                            _hover={
                                                !permission?.update || isOverviewMode
                                                    ? {} // No hover when disabled
                                                    : {
                                                        bg: "#EFF6FF",
                                                        transform: "translateY(-1px)",
                                                        shadow: "md",
                                                        borderColor: "#285AE7",
                                                    }
                                            }
                                            transition="all 0.2s"
                                        >
                                            {placeholder.label}
                                        </Tag>


                                    </WrapItem>
                                ))}
                            </Wrap>

                            <Flex fontSize="12px"
                                fontWeight="400" color="gray.600">
                                <Text
                                    color={"#285AE7!important"} mr={1}>How to use: </Text>
                                <Text
                                    color={" #0D0B26B2!important"}> Type your letter content, then click placeholders to insert them.
                                    Example: "Dear {'{'}CANDIDATE_NAME{'}'}, we are pleased to offer..."</Text>
                            </Flex>
                        </Box>
                    )}

                </VStack>
                <Box p={6} pb={0}>

                    {!showPreview ? (
                        <Box>
                            <TextEditor
                                isDisabled={!permission?.update || isOverviewMode}
                                value={value || ''}
                                handleChange={handleContentChange}  // Changed from onChange to handleChange
                            />
                            <Box mt={2} mb={-3} borderRadius="md">
                                <Flex fontSize="12px"
                                    fontWeight="400" color="gray.600">
                                    <Text
                                        color={"#285AE7!important"} mr={1}> Example: </Text>
                                    <Text
                                        color={" #0D0B26B2!important"}> "We offer you {'{'}JOB_TITLE{'}'} position with {'{'}SALARY{'}'} salary, joining on {'{'}JOINING_DATE{'}'}."</Text>
                                </Flex>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            <Box
                                border="2px solid"
                                borderColor="blue.300"
                                borderRadius="md"
                                p={4}
                                bg="white"
                                minH="300px"
                            >
                                <Flex justify="space-between" align="center" mb={4} pb={2} borderBottom="1px" borderColor="gray.200">
                                    <Text fontSize="md" fontWeight="bold" color="blue.600">
                                        Live Preview
                                    </Text>
                                    <Text fontSize="xs" color="gray.600" bg="yellow.100" px={2} py={1} borderRadius="md">
                                        Read-only
                                    </Text>
                                </Flex>
                                <Box
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent) }}
                                    sx={{
                                        '& p': { marginBottom: '1em', lineHeight: '1.6' },
                                        '& h1, & h2, & h3': { marginTop: '1.5em', marginBottom: '0.75em', fontWeight: 'bold' }
                                    }}
                                />
                            </Box>
                            <Text fontSize="xs" color={" #0D0B26B2!important"} mt={2}>
                                Preview only - switch back to edit mode to modify content
                            </Text>
                        </Box>
                    )}
                </Box>
            </Box> : <Box>
                {showPlaceholders && (
                    <Box mb={3} p={3} bg="gray.50" borderRadius="md">
                        <Flex justify="space-between" align="center" mb={2}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                Dynamic Placeholders
                            </Text>
                            {previewContent && (
                                <Flex align="center" gap={2}>
                                    <FormLabel htmlFor="preview-toggle" mb={0} fontSize="sm">
                                        Preview Mode
                                    </FormLabel>
                                    <Switch
                                        id="preview-toggle"
                                        isChecked={showPreview}
                                        onChange={(e) => setShowPreview(e.target.checked)}
                                        colorScheme="blue"
                                    />
                                </Flex>
                            )}
                        </Flex>

                        <Wrap spacing={2} mb={2}>
                            {defaultPlaceholders.map((placeholder) => (
                                <WrapItem key={placeholder.key}>
                                    <Tag
                                        size="md"
                                        cursor="pointer"
                                        colorScheme="blue"

                                        variant="subtle"
                                        onClick={() => insertPlaceholder(placeholder)}
                                        title={placeholder.description}
                                        _hover={{
                                            bg: 'blue.500',
                                            color: 'white',
                                            transform: 'translateY(-1px)',
                                            shadow: 'md'
                                        }}
                                        transition="all 0.2s"
                                    >
                                        {placeholder.label}
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>

                        <Text fontSize="xs" color="gray.600">
                            <strong>How to use:</strong> Type your letter content, then click placeholders to insert them.
                            Example: "Dear {'{'}CANDIDATE_NAME{'}'}, we are pleased to offer..."
                        </Text>
                    </Box>
                )}

                {!showPreview ? (
                    <Box>
                        <TextEditor
                            isDisabled={isOverviewMode}
                            value={value || ''}
                            handleChange={onChange}
                        />
                        {showExample && <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                            <Text fontSize="xs" color="blue.700">
                                <strong>Example:</strong> "We offer you {'{'}JOB_TITLE{'}'} position with {'{'}SALARY{'}'} salary, joining on {'{'}JOINING_DATE{'}'}."
                            </Text>
                        </Box>}
                    </Box>
                ) : (
                    <Box>
                        <Box
                            border="2px solid"
                            borderColor="blue.300"
                            borderRadius="md"
                            p={4}
                            bg="white"
                            minH="300px"
                        >
                            <Flex justify="space-between" align="center" mb={4} pb={2} borderBottom="1px" borderColor="gray.200">
                                <Text fontSize="md" fontWeight="bold" color="blue.600">
                                    Live Preview
                                </Text>
                                <Text fontSize="xs" color="gray.600" bg="yellow.100" px={2} py={1} borderRadius="md">
                                    Read-only
                                </Text>
                            </Flex>
                            <Box
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent) }}
                                sx={{
                                    '& p': { marginBottom: '1em', lineHeight: '1.6' },
                                    '& h1, & h2, & h3': { marginTop: '1.5em', marginBottom: '0.75em', fontWeight: 'bold' }
                                }}
                            />
                        </Box>
                        <Text fontSize="xs" color="orange.600" mt={2}>
                            Preview only - switch back to edit mode to modify content
                        </Text>
                    </Box>
                )}
            </Box>}

        </>
    );
};

export default OfferContentEditor;